import { Link, Outlet } from "@tanstack/react-router";
import { EyeIcon } from "lucide-react";
import type { Me } from "@/api/me";
import { DatanomyLogo } from "@/components/brand";
import { MeBoundary } from "./MeBoundary";
import { UserMenu } from "./UserMenu";
import { ViewAsBanner } from "./ViewAsBanner";

/** Single-page layout for B2B guest client users (and staff previewing the portal): header + tickets. */
export function PortalLayout() {
  return <MeBoundary>{(me) => <PortalLayoutInner me={me} />}</MeBoundary>;
}

function clientLabel(me: Me): string | null {
  const names = Array.from(
    new Set(
      me.client_scopes
        .map((s) => [s.client_name, s.department_name].filter(Boolean).join(" · "))
        .filter((n) => n.length > 0),
    ),
  );
  return names.length ? names.join(", ") : null;
}

function PortalLayoutInner({ me }: { me: Me }) {
  const client = clientLabel(me);

  return (
    <div className="min-h-dvh bg-background">
      <ViewAsBanner me={me} />
      {me.is_staff && !me.view_as && (
        <div className="flex items-center justify-center gap-2 bg-accent px-4 py-1.5 text-xs font-medium text-accent-foreground">
          <EyeIcon className="size-3.5" aria-hidden="true" />
          You're previewing the client portal as staff.
          <Link to="/" className="underline underline-offset-2">
            Back to Pulse
          </Link>
        </div>
      )}
      <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 md:px-6">
          <Link to="/portal" className="flex items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/40">
            <DatanomyLogo className="md:h-8" />
            <span className="hidden border-l pl-2.5 text-[13px] font-medium text-muted-foreground sm:inline">Client Portal</span>
          </Link>
          {client && (
            <span className="hidden truncate rounded-md border bg-card px-2 py-0.5 text-xs text-muted-foreground md:inline">
              {client}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <UserMenu me={me} />
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
